import {
	EVENTS_SECTION,
	isEventsSlideActive,
	parsePageHash,
	setEventsPageUrl,
} from "./page-hash";

function prefersReducedMotion() {
	return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getEventMoveDurationMs(root) {
	const raw = getComputedStyle(root)
		.getPropertyValue("--paaadel-nav-move-duration")
		.trim();

	if (!raw || raw === "0ms") {
		return 0;
	}

	if (raw.endsWith("ms")) {
		return Number.parseFloat(raw) || 650;
	}

	if (raw.endsWith("s")) {
		return (Number.parseFloat(raw) || 0.65) * 1000;
	}

	return 650;
}

function updateDayButtons(root, eventDate) {
	const date = String(eventDate);

	root
		.querySelectorAll(
			".fullpage-events-calendar__day-btn, .fullpage-events-calendar__mobile-day-btn",
		)
		.forEach((btn) => {
			const active = btn.dataset.eventDate === date;
			btn.classList.toggle("is-selected", active);
			btn.setAttribute("aria-pressed", active ? "true" : "false");
		});
}

function syncMobileNavMonth(root, monthKey) {
	const month = String(monthKey);

	root
		.querySelectorAll(".fullpage-events-calendar__mobile-days-viewport")
		.forEach((viewport) => {
			const active = viewport.dataset.month === month;
			viewport.hidden = !active;
		});

	root
		.querySelectorAll(".fullpage-events-calendar__mobile-days")
		.forEach((days) => {
			const active = days.dataset.month === month;
			days.classList.toggle("is-active", active);
		});

	updateMobileMonthLabel(root, month);
	updateMobileMonthSteps(root);

	const selected = root.querySelector(
		".fullpage-events-calendar__mobile-day-btn.is-selected",
	);

	if (selected?.dataset.eventDate) {
		scrollMobileDayIntoView(root, selected.dataset.eventDate);
	}
}

function getMobileMonthsWithEvents(root) {
	return [
		...root.querySelectorAll(".fullpage-events-calendar__mobile-days-viewport"),
	]
		.filter((viewport) =>
			viewport.querySelector(".fullpage-events-calendar__mobile-day-btn"),
		)
		.map((viewport) => viewport.dataset.month)
		.filter(Boolean);
}

function getCurrentMobileMonth(root) {
	const selected = root.querySelector(
		".fullpage-events-calendar__mobile-day-btn.is-selected",
	);

	if (selected?.dataset.month) {
		return String(selected.dataset.month);
	}

	const activeViewport = root.querySelector(
		".fullpage-events-calendar__mobile-days-viewport:not([hidden])",
	);

	return activeViewport?.dataset.month
		? String(activeViewport.dataset.month)
		: "";
}

function formatMonthLabel(monthKey) {
	const parts = String(monthKey).split("-");
	if (parts.length !== 2) {
		return monthKey;
	}

	const [year, month] = parts.map(Number);
	const lang = document.documentElement.lang || "de-DE";

	return new Intl.DateTimeFormat(lang, {
		month: "long",
		year: "numeric",
	}).format(new Date(year, month - 1, 1));
}

function updateMobileMonthLabel(root, monthKey) {
	const label = root.querySelector("[data-mobile-month-label]");
	if (!label || !monthKey) {
		return;
	}

	label.textContent = formatMonthLabel(monthKey);
}

function updateMobileMonthSteps(root) {
	const months = getMobileMonthsWithEvents(root);
	const current = getCurrentMobileMonth(root);
	const index = months.indexOf(current);

	root
		.querySelectorAll(".fullpage-events-calendar__mobile-month-step")
		.forEach((btn) => {
			const isPrev = btn.dataset.direction === "prev";
			const disabled = isPrev ? index <= 0 : index < 0 || index >= months.length - 1;
			btn.disabled = disabled;
			btn.setAttribute("aria-disabled", disabled ? "true" : "false");
		});
}

function scrollStripItemIntoView(strip, item) {
	if (!strip || !item) {
		return;
	}

	const maxScroll = strip.scrollWidth - strip.clientWidth;
	if (maxScroll <= 0) {
		return;
	}

	const stripRect = strip.getBoundingClientRect();
	const itemRect = item.getBoundingClientRect();
	const delta =
		itemRect.left -
		stripRect.left -
		(stripRect.width - itemRect.width) / 2;

	strip.scrollTo({
		left: Math.max(0, Math.min(maxScroll, strip.scrollLeft + delta)),
		behavior: prefersReducedMotion() ? "auto" : "smooth",
	});
}

function scrollMobileDayIntoView(root, eventDate) {
	const btn = root.querySelector(
		`.fullpage-events-calendar__mobile-day-btn[data-event-date="${eventDate}"]`,
	);
	const strip = btn?.closest("[data-mobile-days-scroll]");

	if (!btn || !strip) {
		return;
	}

	scrollStripItemIntoView(
		strip,
		btn.closest(".fullpage-events-calendar__mobile-day-item") || btn,
	);
}

function activateMobileMonth(root, timeline, monthKey, { pushUrl = false } = {}) {
	const month = String(monthKey);
	if (!month) {
		return;
	}

	expandMonth(root, month, { animate: false });

	const days = root.querySelector(
		`.fullpage-events-calendar__mobile-days[data-month="${month}"]`,
	);
	const selected = days?.querySelector(
		".fullpage-events-calendar__mobile-day-btn.is-selected",
	);
	const target =
		selected || days?.querySelector(".fullpage-events-calendar__mobile-day-btn");

	if (!target?.dataset.eventDate) {
		updateMobileMonthSteps(root);
		return;
	}

	const direction = getTransitionDirection(
		root,
		timeline,
		target.dataset.eventDate,
	);
	selectEvent(root, target.dataset.eventDate, direction);
	updateTimelineSteps(
		root,
		timeline,
		getSelectedTimelineIndex(root, timeline),
	);
	syncEventUrl(target.dataset.eventDate, { push: pushUrl });
	updateMobileMonthSteps(root);
}

function stepMobileMonth(root, timeline, direction) {
	const months = getMobileMonthsWithEvents(root);
	const current = getCurrentMobileMonth(root);
	const index = months.indexOf(current);
	const delta = direction === "prev" ? -1 : 1;
	const nextIndex = index + delta;

	if (nextIndex < 0 || nextIndex >= months.length) {
		return;
	}

	activateMobileMonth(root, timeline, months[nextIndex], { pushUrl: true });
}

function getTransitionDirection(root, timeline, nextEventDate) {
	const currentIndex = getSelectedTimelineIndex(root, timeline);
	const nextIndex = timeline.indexOf(String(nextEventDate));

	if (currentIndex < 0 || nextIndex < 0 || currentIndex === nextIndex) {
		return "none";
	}

	return nextIndex > currentIndex ? "next" : "prev";
}

const LEAVE_CLASSES = ["is-event-leaving--next", "is-event-leaving--prev"];
const ENTER_CLASSES = ["is-event-entering--next", "is-event-entering--prev"];
const eventTransitionTimers = new WeakMap();
const stageHeightUnlockers = new WeakMap();

function resetPanelsScroll(panelsRoot) {
	if (!panelsRoot) {
		return;
	}

	const active = panelsRoot.querySelector(
		".fullpage-events-calendar__panel.is-event-active",
	);

	if (active) {
		active.scrollTop = 0;
		return;
	}

	panelsRoot.scrollTop = 0;
}

function lockPanelsStageHeight(stage, ...panels) {
	if (!stage) {
		return () => {};
	}

	const heights = panels.filter(Boolean).map((panel) => panel.offsetHeight);
	heights.push(stage.offsetHeight);

	const height = Math.max(...heights, 0);
	if (height <= 0) {
		return () => {};
	}

	stage.style.minHeight = `${height}px`;

	return () => {
		stage.style.minHeight = "";
	};
}

function clearPanelMotionClasses(panel) {
	panel.classList.remove(...LEAVE_CLASSES, ...ENTER_CLASSES);
}

function applyInstantEvent(root, eventDate) {
	const stage = root.querySelector(".fullpage-events-calendar__panels-stage");
	if (!stage) {
		return;
	}

	const date = String(eventDate);

	stage
		.querySelectorAll(".fullpage-events-calendar__panel")
		.forEach((panel) => {
			const active = panel.dataset.eventDate === date;
			clearPanelMotionClasses(panel);
			panel.classList.toggle("is-event-active", active);
			panel.hidden = !active;
		});
	updateDayButtons(root, date);
	scrollMobileDayIntoView(root, date);
	resetPanelsScroll(root.querySelector(".fullpage-events-calendar__panels"));
}

function cancelEventTransition(panelsRoot, stage) {
	const timer = eventTransitionTimers.get(panelsRoot);
	if (timer) {
		window.clearTimeout(timer);
		eventTransitionTimers.delete(panelsRoot);
	}

	panelsRoot.classList.remove("is-event-transitioning");
	delete panelsRoot.dataset.eventTransitioning;

	const unlockHeight = stageHeightUnlockers.get(panelsRoot);
	if (unlockHeight) {
		unlockHeight();
		stageHeightUnlockers.delete(panelsRoot);
	}

	stage
		?.querySelectorAll(".fullpage-events-calendar__panel")
		.forEach((panel) => {
			clearPanelMotionClasses(panel);
		});
}

function selectEvent(root, eventDate, direction = "none") {
	const date = String(eventDate);
	const stage = root.querySelector(".fullpage-events-calendar__panels-stage");
	const panelsRoot = root.querySelector(".fullpage-events-calendar__panels");

	if (panelsRoot?.dataset.eventTransitioning === "true") {
		cancelEventTransition(panelsRoot, stage);
		applyInstantEvent(root, date);
		return;
	}

	const current = stage?.querySelector(
		".fullpage-events-calendar__panel.is-event-active",
	);
	const next = stage?.querySelector(
		`.fullpage-events-calendar__panel[data-event-date="${date}"]`,
	);

	if (!stage || !next || current === next) {
		applyInstantEvent(root, date);
		return;
	}

	const duration = getEventMoveDurationMs(root);

	if (prefersReducedMotion() || duration <= 0 || direction === "none") {
		applyInstantEvent(root, date);
		return;
	}

	const leaveClass =
		direction === "next"
			? "is-event-leaving--next"
			: "is-event-leaving--prev";
	const enterClass =
		direction === "next"
			? "is-event-entering--next"
			: "is-event-entering--prev";

	updateDayButtons(root, date);
	scrollMobileDayIntoView(root, date);
	panelsRoot.dataset.eventTransitioning = "true";
	panelsRoot.classList.add("is-event-transitioning");

	const unlockHeight = lockPanelsStageHeight(stage, current, next);
	stageHeightUnlockers.set(panelsRoot, unlockHeight);

	clearPanelMotionClasses(next);
	clearPanelMotionClasses(current);
	next.hidden = false;
	next.classList.add(enterClass);

	requestAnimationFrame(() => {
		current.classList.add(leaveClass);
		next.classList.add("is-event-active");
		next.classList.remove(enterClass);
		current.classList.remove("is-event-active");
	});

	const finish = () => {
		eventTransitionTimers.delete(panelsRoot);
		clearPanelMotionClasses(current);
		current.hidden = true;
		panelsRoot.classList.remove("is-event-transitioning");
		delete panelsRoot.dataset.eventTransitioning;

		const releaseHeight = stageHeightUnlockers.get(panelsRoot);
		if (releaseHeight) {
			releaseHeight();
			stageHeightUnlockers.delete(panelsRoot);
		}

		resetPanelsScroll(panelsRoot);
	};

	eventTransitionTimers.set(
		panelsRoot,
		window.setTimeout(finish, duration + 50),
	);
}

const monthDaysTimers = new WeakMap();
const yearMonthsTimers = new WeakMap();

function getYearForMonth(monthKey) {
	return String(monthKey).slice(0, 4);
}

function setYearExpandedState(block, expanded) {
	const btn = block.querySelector(".fullpage-events-calendar__year-btn");
	const months = block.querySelector(
		".fullpage-events-calendar__year-months",
	);

	block.classList.toggle("is-expanded", expanded);

	if (btn) {
		btn.setAttribute("aria-expanded", expanded ? "true" : "false");
	}

	if (months) {
		months.setAttribute("aria-hidden", expanded ? "false" : "true");
	}
}

function clearYearMonthsInlineStyles(months) {
	months.style.removeProperty("max-height");
	months.style.removeProperty("overflow");
}

function animateYearMonths(block, expand, root) {
	const months = block.querySelector(
		".fullpage-events-calendar__year-months",
	);
	const btn = block.querySelector(".fullpage-events-calendar__year-btn");

	if (!months || !btn) {
		return;
	}

	const pending = yearMonthsTimers.get(months);
	if (pending) {
		window.clearTimeout(pending);
		yearMonthsTimers.delete(months);
	}

	const duration = getEventMoveDurationMs(root);

	if (prefersReducedMotion() || duration <= 0) {
		clearYearMonthsInlineStyles(months);
		setYearExpandedState(block, expand);
		return;
	}

	months.style.overflow = "hidden";

	if (expand) {
		setYearExpandedState(block, true);
		months.style.maxHeight = "0";
		months.offsetHeight;
		months.style.maxHeight = `${months.scrollHeight}px`;

		yearMonthsTimers.set(
			months,
			window.setTimeout(() => {
				if (block.classList.contains("is-expanded")) {
					clearYearMonthsInlineStyles(months);
				}

				yearMonthsTimers.delete(months);
			}, duration + 50),
		);
		return;
	}

	months.style.maxHeight = `${months.scrollHeight}px`;
	months.offsetHeight;
	setYearExpandedState(block, false);
	months.style.maxHeight = "0";

	yearMonthsTimers.set(
		months,
		window.setTimeout(() => {
			clearYearMonthsInlineStyles(months);
			yearMonthsTimers.delete(months);
		}, duration + 50),
	);
}

function expandYear(root, year, { animate = true } = {}) {
	const yearKey = String(year);

	root.querySelectorAll(".fullpage-events-calendar__year-block").forEach(
		(block) => {
			const btn = block.querySelector(
				".fullpage-events-calendar__year-btn",
			);
			const isTarget = btn?.dataset.year === yearKey;
			const isExpanded = block.classList.contains("is-expanded");

			if (!animate) {
				const months = block.querySelector(
					".fullpage-events-calendar__year-months",
				);
				if (months) {
					clearYearMonthsInlineStyles(months);
				}

				setYearExpandedState(block, isTarget);
				return;
			}

			if (isTarget && !isExpanded) {
				animateYearMonths(block, true, root);
			} else if (!isTarget && isExpanded) {
				animateYearMonths(block, false, root);
			}
		},
	);
}

function toggleYear(root, year, { animate = true } = {}) {
	const yearKey = String(year);
	const block = root.querySelector(
		`.fullpage-events-calendar__year-btn[data-year="${yearKey}"]`,
	)?.closest(".fullpage-events-calendar__year-block");

	if (!block) {
		return;
	}

	if (block.classList.contains("is-expanded")) {
		animateYearMonths(block, false, root);
		return;
	}

	expandYear(root, yearKey, { animate });
}

function setMonthExpandedState(block, expanded) {
	const btn = block.querySelector(".fullpage-events-calendar__month-btn");
	const days = block.querySelector(".fullpage-events-calendar__days");

	block.classList.toggle("is-expanded", expanded);

	if (btn) {
		btn.setAttribute("aria-expanded", expanded ? "true" : "false");
	}

	if (days) {
		days.setAttribute("aria-hidden", expanded ? "false" : "true");
	}
}

function clearMonthDaysInlineStyles(days) {
	days.style.removeProperty("max-height");
	days.style.removeProperty("overflow");
}

function animateMonthDays(block, expand, root) {
	const days = block.querySelector(".fullpage-events-calendar__days");
	const btn = block.querySelector(".fullpage-events-calendar__month-btn");

	if (!days || !btn || btn.getAttribute("aria-disabled") === "true") {
		return;
	}

	const pending = monthDaysTimers.get(days);
	if (pending) {
		window.clearTimeout(pending);
		monthDaysTimers.delete(days);
	}

	const duration = getEventMoveDurationMs(root);

	if (prefersReducedMotion() || duration <= 0) {
		clearMonthDaysInlineStyles(days);
		setMonthExpandedState(block, expand);
		return;
	}

	days.style.overflow = "hidden";

	if (expand) {
		setMonthExpandedState(block, true);
		days.style.maxHeight = "0";
		// Force reflow so the browser picks up the collapsed height.
		days.offsetHeight;
		days.style.maxHeight = `${days.scrollHeight}px`;

		monthDaysTimers.set(
			days,
			window.setTimeout(() => {
				if (block.classList.contains("is-expanded")) {
					clearMonthDaysInlineStyles(days);
				}

				monthDaysTimers.delete(days);
			}, duration + 50),
		);
		return;
	}

	days.style.maxHeight = `${days.scrollHeight}px`;
	days.offsetHeight;
	setMonthExpandedState(block, false);
	days.style.maxHeight = "0";

	monthDaysTimers.set(
		days,
		window.setTimeout(() => {
			clearMonthDaysInlineStyles(days);
			monthDaysTimers.delete(days);
		}, duration + 50),
	);
}

function expandMonth(root, month, { animate = true } = {}) {
	const monthKey = String(month);

	expandYear(root, getYearForMonth(monthKey), { animate });
	syncMobileNavMonth(root, monthKey);

	root.querySelectorAll(".fullpage-events-calendar__month-block").forEach(
		(block) => {
			const btn = block.querySelector(
				".fullpage-events-calendar__month-btn",
			);
			const isTarget =
				btn?.dataset.month === monthKey &&
				btn.getAttribute("aria-disabled") !== "true";
			const isExpanded = block.classList.contains("is-expanded");

			if (!animate) {
				const days = block.querySelector(
					".fullpage-events-calendar__days",
				);
				if (days) {
					clearMonthDaysInlineStyles(days);
				}

				setMonthExpandedState(block, isTarget);
				return;
			}

			if (isTarget && !isExpanded) {
				animateMonthDays(block, true, root);
			} else if (!isTarget && isExpanded) {
				animateMonthDays(block, false, root);
			}
		},
	);
}

function getTimeline(root) {
	const idsAttr = root.dataset.timelineIds;
	if (idsAttr) {
		return idsAttr.split(",").filter(Boolean);
	}

	return [...root.querySelectorAll(".fullpage-events-calendar__day-btn")]
		.map((btn) => btn.dataset.eventDate)
		.filter(Boolean);
}

function getSelectedTimelineIndex(root, timeline) {
	const selected = root.querySelector(
		".fullpage-events-calendar__day-btn.is-selected, .fullpage-events-calendar__mobile-day-btn.is-selected",
	);
	if (!selected?.dataset.eventDate) {
		return -1;
	}

	return timeline.indexOf(String(selected.dataset.eventDate));
}

function updateTimelineSteps(root, timeline, index) {
	const atStart = index <= 0;
	const atEnd = index < 0 || index >= timeline.length - 1;

	root.querySelectorAll(".fullpage-events-calendar__timeline-step").forEach(
		(btn) => {
			const isPrev = btn.dataset.direction === "prev";
			const disabled = isPrev ? atStart : atEnd;
			btn.disabled = disabled;
			btn.setAttribute("aria-disabled", disabled ? "true" : "false");
		},
	);
}

function activateTimelineIndex(
	root,
	timeline,
	index,
	direction,
	{ pushUrl = false } = {},
) {
	if (index < 0 || index >= timeline.length) {
		return;
	}

	const eventDate = timeline[index];
	const dayBtn = root.querySelector(
		`.fullpage-events-calendar__day-btn[data-event-date="${eventDate}"], .fullpage-events-calendar__mobile-day-btn[data-event-date="${eventDate}"]`,
	);

	if (!dayBtn) {
		return;
	}

	if (dayBtn.dataset.month) {
		expandMonth(root, dayBtn.dataset.month);
	}

	selectEvent(root, eventDate, direction);
	updateTimelineSteps(root, timeline, index);
	syncEventUrl(eventDate, { push: pushUrl });

	dayBtn.scrollIntoView({
		block: "nearest",
		behavior: prefersReducedMotion() ? "auto" : "smooth",
	});
	scrollMobileDayIntoView(root, eventDate);
}

function stepTimeline(root, timeline, direction) {
	const currentIndex = getSelectedTimelineIndex(root, timeline);
	const startIndex = currentIndex < 0 ? 0 : currentIndex;
	const delta = direction === "prev" ? -1 : 1;
	const nextIndex = startIndex + delta;

	if (nextIndex < 0 || nextIndex >= timeline.length) {
		return;
	}

	activateTimelineIndex(
		root,
		timeline,
		nextIndex,
		direction === "prev" ? "prev" : "next",
		{ pushUrl: true },
	);
}

const SWIPE_MIN_DISTANCE = 48;
const SWIPE_AXIS_RATIO = 1.25;
const MOBILE_LAYOUT_MQ = "(max-width: 599px)";

function bindEventsCalendarSwipe(root, timeline) {
	if (window.matchMedia(MOBILE_LAYOUT_MQ).matches) {
		return;
	}
	const swipeEl = root.querySelector(".fullpage-events-calendar__detail");
	if (!swipeEl) {
		return;
	}

	let startX = 0;
	let startY = 0;
	let tracking = false;
	let horizontalSwipe = false;

	const resetSwipe = () => {
		tracking = false;
		horizontalSwipe = false;
		swipeEl.classList.remove("is-swipe-tracking");
	};

	const onPointerDown = (event) => {
		if (event.pointerType === "mouse" && event.button !== 0) {
			return;
		}

		if (event.target.closest("a, button")) {
			return;
		}

		startX = event.clientX;
		startY = event.clientY;
		tracking = true;
		horizontalSwipe = false;
	};

	const onPointerMove = (event) => {
		if (!tracking || horizontalSwipe) {
			return;
		}

		const deltaX = event.clientX - startX;
		const deltaY = event.clientY - startY;

		if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) {
			return;
		}

		if (Math.abs(deltaY) > Math.abs(deltaX) * SWIPE_AXIS_RATIO) {
			resetSwipe();
			return;
		}

		if (Math.abs(deltaX) > Math.abs(deltaY) * SWIPE_AXIS_RATIO) {
			horizontalSwipe = true;
			swipeEl.classList.add("is-swipe-tracking");
		}
	};

	const onPointerUp = (event) => {
		if (!tracking) {
			return;
		}

		const deltaX = event.clientX - startX;
		const deltaY = event.clientY - startY;
		const shouldSwipe =
			horizontalSwipe &&
			Math.abs(deltaX) >= SWIPE_MIN_DISTANCE &&
			Math.abs(deltaX) > Math.abs(deltaY);

		resetSwipe();

		if (!shouldSwipe) {
			return;
		}

		stepTimeline(root, timeline, deltaX < 0 ? "next" : "prev");
	};

	swipeEl.addEventListener("pointerdown", onPointerDown, { passive: true });
	swipeEl.addEventListener("pointermove", onPointerMove, { passive: true });
	swipeEl.addEventListener("pointerup", onPointerUp, { passive: true });
	swipeEl.addEventListener("pointercancel", onPointerUp, { passive: true });
}

function resolveEventFromUrl(root) {
	const { section, eventDate } = parsePageHash();

	if (section !== EVENTS_SECTION || !eventDate) {
		return null;
	}

	const timeline = getTimeline(root);

	if (!timeline.includes(eventDate)) {
		return null;
	}

	const dayBtn = root.querySelector(
		`.fullpage-events-calendar__day-btn[data-event-date="${eventDate}"], .fullpage-events-calendar__mobile-day-btn[data-event-date="${eventDate}"]`,
	);

	return {
		eventDate,
		month: dayBtn?.dataset.month || root.dataset.initialMonth,
	};
}

function resolveDefaultSelection(root) {
	const today = root.dataset.calendarToday;
	const todayBtn = today
		? root.querySelector(
				`.fullpage-events-calendar__day-btn[data-event-date="${today}"], .fullpage-events-calendar__mobile-day-btn[data-event-date="${today}"]`,
			)
		: null;

	if (todayBtn?.dataset.eventDate) {
		return {
			eventDate: todayBtn.dataset.eventDate,
			month: todayBtn.dataset.month || root.dataset.initialMonth,
		};
	}

	return {
		eventDate: root.dataset.initialEventDate,
		month: root.dataset.initialMonth,
	};
}

function resolveInitialSelection(root) {
	const fromUrl = resolveEventFromUrl(root);
	if (fromUrl) {
		return fromUrl;
	}

	return resolveDefaultSelection(root);
}

function resetCalendarToDefault(root, { forceUrl = false } = {}) {
	const timeline = getTimeline(root);
	const { eventDate, month } = resolveDefaultSelection(root);

	if (month) {
		expandMonth(root, month, { animate: false });
	}

	if (!eventDate) {
		return;
	}

	applyInstantEvent(root, eventDate);
	const index = timeline.indexOf(String(eventDate));
	updateTimelineSteps(root, timeline, index >= 0 ? index : 0);
	syncEventUrl(eventDate, { force: forceUrl });
}

function activateEventDateInstant(root, timeline, eventDate) {
	const dayBtn = root.querySelector(
		`.fullpage-events-calendar__day-btn[data-event-date="${eventDate}"], .fullpage-events-calendar__mobile-day-btn[data-event-date="${eventDate}"]`,
	);

	if (dayBtn?.dataset.month) {
		expandMonth(root, dayBtn.dataset.month, { animate: false });
	}

	applyInstantEvent(root, eventDate);
	updateTimelineSteps(root, timeline, timeline.indexOf(String(eventDate)));
}

function handleEventsSlideEnter() {
	document.querySelectorAll("[data-events-calendar]").forEach((root) => {
		const { eventDate } = parsePageHash();
		const timeline = getTimeline(root);

		if (eventDate && timeline.includes(eventDate)) {
			activateEventDateInstant(root, timeline, eventDate);
			syncEventUrl(eventDate, { force: true });
			return;
		}

		resetCalendarToDefault(root, { forceUrl: true });
	});
}

function syncEventUrl(eventDate, { push = false, force = false } = {}) {
	if (!eventDate || (!force && !isEventsSlideActive())) {
		return;
	}

	setEventsPageUrl(eventDate, { push });
}

function initCalendarRoot(root) {
	const timeline = getTimeline(root);
	const { eventDate, month } = resolveInitialSelection(root);

	if (month) {
		expandMonth(root, month, { animate: false });
	}

	if (eventDate) {
		applyInstantEvent(root, eventDate);
		syncEventUrl(eventDate);
	}

	const initialIndex = getSelectedTimelineIndex(root, timeline);
	updateTimelineSteps(root, timeline, initialIndex < 0 ? 0 : initialIndex);
	updateMobileMonthSteps(root);

	const bindTimelineStep = (btn) => {
		btn.addEventListener("click", () => {
			if (btn.disabled) {
				return;
			}

			stepTimeline(root, timeline, btn.dataset.direction);
		});
	};

	root.querySelectorAll(".fullpage-events-calendar__timeline-step").forEach(
		bindTimelineStep,
	);

	root.querySelectorAll(".fullpage-events-calendar__mobile-month-step").forEach(
		(btn) => {
			btn.addEventListener("click", () => {
				if (btn.disabled) {
					return;
				}

				stepMobileMonth(root, timeline, btn.dataset.direction);
			});
		},
	);

	root.querySelectorAll(".fullpage-events-calendar__mobile-day-btn").forEach(
		(btn) => {
			btn.addEventListener("click", () => {
				const { month: monthKey, eventDate: date } = btn.dataset;
				if (monthKey) {
					expandMonth(root, monthKey);
				}

				const direction = getTransitionDirection(root, timeline, date);
				selectEvent(root, date, direction);
				updateTimelineSteps(
					root,
					timeline,
					getSelectedTimelineIndex(root, timeline),
				);
				syncEventUrl(date, { push: true });
			});
		},
	);

	root.querySelectorAll(".fullpage-events-calendar__year-btn").forEach(
		(btn) => {
			btn.addEventListener("click", () => {
				const { year } = btn.dataset;
				if (year) {
					toggleYear(root, year);
				}
			});
		},
	);

	root.querySelectorAll(".fullpage-events-calendar__month-btn").forEach(
		(btn) => {
			btn.addEventListener("click", () => {
				if (btn.getAttribute("aria-disabled") === "true") {
					return;
				}

				const { month: monthKey } = btn.dataset;
				expandMonth(root, monthKey);

				const days = btn.parentElement?.querySelector(
					".fullpage-events-calendar__days",
				);
				const selected = days?.querySelector(
					".fullpage-events-calendar__day-btn.is-selected",
				);
				const target =
					selected ||
					days?.querySelector(".fullpage-events-calendar__day-btn");

				if (target) {
					const direction = getTransitionDirection(
						root,
						timeline,
						target.dataset.eventDate,
					);
					selectEvent(root, target.dataset.eventDate, direction);
					updateTimelineSteps(
						root,
						timeline,
						getSelectedTimelineIndex(root, timeline),
					);
					syncEventUrl(target.dataset.eventDate, { push: true });
				}
			});
		},
	);

	root.querySelectorAll(".fullpage-events-calendar__day-btn").forEach(
		(btn) => {
			btn.addEventListener("click", () => {
				const { month: monthKey, eventDate } = btn.dataset;
				if (monthKey) {
					expandMonth(root, monthKey);
				}

				const direction = getTransitionDirection(
					root,
					timeline,
					eventDate,
				);
				selectEvent(root, eventDate, direction);
				updateTimelineSteps(
					root,
					timeline,
					getSelectedTimelineIndex(root, timeline),
				);
				syncEventUrl(eventDate, { push: true });
			});
		},
	);

	bindEventsCalendarSwipe(root, timeline);
}

function bindEventsCalendarUrlSync() {
	window.addEventListener("popstate", () => {
		const { section, eventDate } = parsePageHash();

		if (section !== EVENTS_SECTION || !eventDate) {
			return;
		}

		document.querySelectorAll("[data-events-calendar]").forEach((root) => {
			const timeline = getTimeline(root);
			if (timeline.includes(eventDate)) {
				activateEventDateInstant(root, timeline, eventDate);
			}
		});
	});
}

function bindEventsCalendarSlideReset() {
	document.addEventListener("paaadel:fullpage-slide-change", (event) => {
		const section = event.detail?.section || "";
		const previousSection = event.detail?.previousSection || "";

		if (
			section === EVENTS_SECTION &&
			previousSection !== "" &&
			previousSection !== EVENTS_SECTION
		) {
			handleEventsSlideEnter();
		}
	});
}

export function initEventsCalendar() {
	document
		.querySelectorAll("[data-events-calendar]")
		.forEach(initCalendarRoot);
	bindEventsCalendarUrlSync();
	bindEventsCalendarSlideReset();
}
